import Tooltip, { type TooltipProps } from "@mui/material/Tooltip";
import { Dayjs } from "dayjs";
import type { CSSProperties, FC, ReactElement, ReactNode } from "react";
import type { ServerStream } from "src/types/work-packets";
import type { ActivationState } from "./activation-status-types";
import { SunsetBannerBody, SunsetBannerTitle } from "./SunsetBannerText";
import { DeactivationBannerBody, DeactivationBannerTitle } from "./DeactivationBannerText";
import { ExpiredBannerBody, ExpiredBannerTitle } from "./ExpiredBannerText";
import { MigratedBannerBody, MigratedBannerTitle } from "./MigratedBannerText";

export interface ActivationStatusTooltipProps {
  activationState: ActivationState;
  deactivationDate?: Date;
  recoveryStream: ServerStream;
  expiredAt: Dayjs | undefined;
  migratedAt: Dayjs | undefined;
  children: ReactElement;
}

const tooltipSlotProps: TooltipProps["slotProps"] = {
  tooltip: { sx: { scale: 1.25 } },
  // Lift the tooltip clear of the cursor, which otherwise sits on top of the first line
  popper: { modifiers: [{ name: "offset", options: { offset: [0, 18] } }] },
};
const hrStyle: CSSProperties = { marginBlock: "2px" };

export const ActivationStatusTooltip: FC<ActivationStatusTooltipProps> = ({
  activationState,
  deactivationDate,
  recoveryStream,
  expiredAt,
  migratedAt,
  children,
}) => {
  const tooltipContent = getTooltipContent(activationState, deactivationDate, recoveryStream, expiredAt, migratedAt);
  return (
    <Tooltip title={tooltipContent} followCursor placement="top" slotProps={tooltipSlotProps}>
      {children}
    </Tooltip>
  );
};

interface ActivationStatusTooltipContentProps {
  title: ReactNode;
  subtitle?: ReactNode;
  body: ReactNode;
}

const ActivationStatusTooltipContent: FC<ActivationStatusTooltipContentProps> = ({ title, subtitle, body }) => (
  <div>
    <p>
      <b>{title}</b>
    </p>
    {subtitle && (
      <p>
        <i>{subtitle}</i>
      </p>
    )}
    <hr style={hrStyle} />
    <p>{body}</p>
  </div>
);

function getTooltipContent(
  state: ActivationState,
  deactivationDate: Date | undefined,
  stream: ServerStream,
  expiredAt: Dayjs | undefined,
  migratedAt: Dayjs | undefined,
) {
  // Migrated takes precedence: the packet is read-only regardless of store or expiry state
  if (migratedAt) {
    return (
      <ActivationStatusTooltipContent
        title={<MigratedBannerTitle />}
        subtitle={<>Since {migratedAt.toDate().toLocaleDateString("en-us", { dateStyle: "medium" })}</>}
        body={<MigratedBannerBody />}
      />
    );
  }

  if (state === "inactive") {
    return <ActivationStatusTooltipContent title={<DeactivationBannerTitle />} body={<DeactivationBannerBody />} />;
  }

  if (state === "sunset") {
    return (
      <ActivationStatusTooltipContent
        title={<SunsetBannerTitle scope={stream} />}
        subtitle={<>Since {deactivationDate?.toLocaleDateString("en-us", { dateStyle: "medium" })}</>}
        body={<SunsetBannerBody />}
      />
    );
  }

  if (expiredAt) {
    return (
      <ActivationStatusTooltipContent
        title={<ExpiredBannerTitle />}
        subtitle={<>Since {expiredAt?.toDate()?.toLocaleDateString("en-us", { dateStyle: "medium" })}</>}
        body={<ExpiredBannerBody />}
      />
    );
  }
}
